import { useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { toRaiseInput, type RaiseWorkOrderFormValues } from '../policies/raise-work-order.policy';
import { workOrderNumber } from '../policies/work-order-labels';
import { raiseWorkOrder } from '../work-orders.api';

/** The raise drawer's open state and submit. A refusal stays inline in the drawer; a raised order toasts and reloads the list. */
export function useRaiseWorkOrderDrawer(reload: () => Promise<void>) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  async function submit(values: RaiseWorkOrderFormValues): Promise<boolean> {
    const built = toRaiseInput(values);
    if (!built.ok) {
      setError(built.reason);
      return false;
    }
    setError(null);
    setSubmitting(true);
    try {
      const order = await raiseWorkOrder(built.input);
      await reload();
      toast.success(`Work order ${workOrderNumber(order.sequenceNumber)} raised`);
      return true;
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Could not raise the work order — check the fields and try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  return {
    openDrawer: () => {
      setError(null);
      setOpen(true);
    },
    drawerProps: { open, error, submitting, onSubmit: submit, onClose: () => setOpen(false) },
  };
}
